/**
 * Create Inspection Dialog - New Implementation
 *
 * Create a new inspection type from scratch.
 * Simplified without language dictionary.
 */
import React, { useState, useEffect } from "react";
import { useIntl } from "react-intl";
import { Modal, Button, Input, Checkbox, Select, TextArea } from "@gpstrackit/gtc-react-controls";
import { Box, Typography } from "@mui/material";
import { getInspectionCategories } from "../../../../../../services-px/inspectionApi";

const NAME_REGEX = /^[a-zA-Z0-9\s\-_]+$/;

/**
 * Dialog for creating a new inspection type from scratch
 */
const CreateInspectionDialog = ({ open, onClose, onCreate, existingNames }) => {
	const { messages } = useIntl();

	const [categories, setCategories] = useState([]);
	const [categoriesLoading, setCategoriesLoading] = useState(false);

	useEffect(() => {
		if (open) {
			setCategoriesLoading(true);
			getInspectionCategories()
				.then((data) => {
					const mappedCategories = data.map((cat) => ({
						value: cat.id,
						label: cat.name,
					}));
					setCategories(mappedCategories);
				})
				.catch((error) => {
					console.error("Failed to fetch inspection categories:", error);
					setCategories([]);
				})
				.finally(() => {
					setCategoriesLoading(false);
				});
		}
	}, [open]);

	const [formData, setFormData] = useState({
		name: "",
		description: "",
		category: "",
		zoneless: false,
		enforceOrder: false,
		legallyMandated: false,
		prepopulateDefects: true,
		associateDriver: true,
	});
	const [errors, setErrors] = useState({});

	const handleChange = (field, value) => {
		setFormData((prev) => ({ ...prev, [field]: value }));

		// Clear error for this field
		if (errors[field]) {
			setErrors((prev) => ({ ...prev, [field]: null }));
		}
	};

	const validate = () => {
		const newErrors = {};

		// Validate name
		if (!formData.name.trim()) {
			newErrors.name = messages.nameIsRequired || "Name is required";
		} else if (!NAME_REGEX.test(formData.name)) {
			newErrors.name =
				messages.nameInvalidCharacters || "Name can only contain letters, numbers, spaces, hyphens, and underscores";
		} else if (
			existingNames.some(
				(name) => name.toLowerCase() === formData.name.toLowerCase()
			)
		) {
			newErrors.name = messages.inspectionNameAlreadyExists || "An inspection with this name already exists";
		}

		// Validate description
		if (!formData.description.trim()) {
			newErrors.description = messages.descriptionIsRequired || "Description is required";
		} else if (!NAME_REGEX.test(formData.description)) {
			newErrors.description =
				messages.descriptionInvalidCharacters || "Description can only contain letters, numbers, spaces, hyphens, and underscores";
		}

		// Validate category
		if (!formData.category) {
			newErrors.category = messages.categoryIsRequired || "Category is required";
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleCreate = () => {
		if (validate()) {
			onCreate(formData);
			handleClose();
		}
	};

	const handleClose = () => {
		setFormData({
			name: "",
			description: "",
			category: "",
			zoneless: false,
			enforceOrder: false,
			legallyMandated: false,
			prepopulateDefects: true,
			associateDriver: true,
		});
		setErrors({});
		onClose();
	};

	return (
		<Modal
			title={messages.newInspectionType || "New Inspection Type"}
			open={open}
			handleClose={handleClose}
			size="small"
			footer={
				<div>
					<Button
						variant="secondary"
						onClick={handleClose}
						style={{ marginRight: "5px" }}
					>
						{messages.cancel || "Cancel"}
					</Button>
					<Button
						variant="primary"
						onClick={handleCreate}
					>
						{messages.create || "Create"}
					</Button>
				</div>
			}
		>
			<div style={{ padding: "15px" }}>
				<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
					<div>
						<Input
							label={messages.nameRequired || "Name *"}
							value={formData.name}
							onChange={(value) => handleChange("name", value)}
							required
							maxLength={50}
						/>
						{errors.name && (
							<Typography variant="caption" color="error">
								{errors.name}
							</Typography>
						)}
					</div>

					<div>
						<TextArea
							label={messages.descriptionRequired || "Description *"}
							value={formData.description}
							onChange={(value) => handleChange("description", value)}
							rows={2}
							required
						/>
						{errors.description && (
							<Typography variant="caption" color="error">
								{errors.description}
							</Typography>
						)}
					</div>

					<div>
						<Select
							id="category-select"
							label={messages.categoryRequired || "Category *"}
							placeholder={messages.selectCategory || "Select Category"}
							value={categories.find((c) => c.value === formData.category) || null}
							onChange={(selected) => {
								handleChange("category", selected?.value || "");
								handleChange("categoryName", selected?.label || "");
							}}
							options={categories}
							textField="label"
							valueField="value"
							required
							isLoading={categoriesLoading}
						/>
						{errors.category && (
							<Typography variant="caption" color="error">
								{errors.category}
							</Typography>
						)}
					</div>

					<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
						<Checkbox
							id="zoneless-checkbox"
							label={messages.zonelessDescription || "Zoneless (not tied to specific zones)"}
							checked={formData.zoneless}
							onChange={(checked) => handleChange("zoneless", checked)}
						/>

						<Checkbox
							id="enforceOrder-checkbox"
							label={messages.enforceFieldOrder || "Enforce field order"}
							checked={formData.enforceOrder}
							onChange={(checked) => handleChange("enforceOrder", checked)}
						/>

						<Checkbox
							id="legallyMandated-checkbox"
							label={messages.legallyMandatedDescription || "Legally mandated (DOT/regulatory)"}
							checked={formData.legallyMandated}
							onChange={(checked) => handleChange("legallyMandated", checked)}
						/>

						<Checkbox
							id="prepopulateDefects-checkbox"
							label={messages.prepopulateDefectsDescription || "Pre-populate defects from previous inspection"}
							checked={formData.prepopulateDefects}
							onChange={(checked) => handleChange("prepopulateDefects", checked)}
						/>

						<Checkbox
							id="associateDriver-checkbox"
							label={messages.associateWithDriver || "Associate with driver"}
							checked={formData.associateDriver}
							onChange={(checked) => handleChange("associateDriver", checked)}
						/>
					</Box>
				</Box>
			</div>
		</Modal>
	);
};

export default CreateInspectionDialog;
